import { escapeAttribute, formatFloat, ProjectFile } from '../../core/hp/projectFile';

/** A catalog piece in the file's own terms: plane-local point, file angle and PlaneOffset, sizes as the file holds them. */
export interface CatalogFurniture {
  uniqueId: string;
  size: { w: number; h: number; d: number };
  rotation: number;
  planeOffset: number;
  position: { x: number; y: number };
  scale: [number, number, number];
  pro: boolean;
  info: { id: string; furnitureId: string; price?: number; caption?: string };
}

/** What a rewrite changes; a zero rotation or PlaneOffset drops the attribute, as the app skips a zero. */
export interface FurnitureChange {
  uniqueId?: string;
  position?: { x: number; y: number };
  rotation?: number;
  planeOffset?: number;
}

const isZero = (value: number) => Math.fround(value) === 0;
const number = (name: string, value: number | undefined) => value === undefined || isZero(value) ? '' : ` ${name}="${formatFloat(value)}"`;
const text = (name: string, value: string | undefined) => value ? ` ${name}="${escapeAttribute(value)}"` : '';

/** The `<Furniture>` the app's `Furniture.SerializeInner` writes for a piece placed from the catalog. */
export function catalogFurnitureXml(piece: CatalogFurniture): string {
  const { size, position, scale, info } = piece;
  const point = isZero(position.x) && isZero(position.y) ? '' : `<Position X="${formatFloat(position.x)}" Y="${formatFloat(position.y)}" />`;
  const scaled = scale.every(axis => Math.fround(axis) === 1) ? '' : `<Scale x="${formatFloat(scale[0])}" y="${formatFloat(scale[1])}" z="${formatFloat(scale[2])}" />`;
  return `<Furniture UniqueId="${escapeAttribute(piece.uniqueId)}"${number('Depth', size.d)}${number('Height', size.h)}${number('Width', size.w)}`
    + `${number('Rotation', piece.rotation)}${number('PlaneOffset', piece.planeOffset)} AddedFromCatalog="True" AddedAsPro="${piece.pro ? 'True' : 'False'}" Data="">`
    + `${point}${scaled}<Info Id="${escapeAttribute(info.id)}"${number('Price', info.price)}${text('Caption', info.caption)}`
    + ` FurnitureId="${escapeAttribute(info.furnitureId)}" CustomPrice="-1" /></Furniture>`;
}

/** One `<Furniture>` as `ProjectFile.xmlOf` gives it, with the changed values set and every other byte kept. */
export function rewriteFurnitureXml(xml: string, change: FurnitureChange): string {
  const scratch = ProjectFile.read(`<Project>${xml}</Project>`), piece = scratch.element([['Furniture', 0]]);
  if (change.uniqueId !== undefined) scratch.setText(piece, 'UniqueId', change.uniqueId);
  if (change.position) scratch.setPosition(piece, change.position.x, change.position.y);
  if (change.rotation !== undefined) scratch.setRotation(piece, change.rotation);
  if (change.planeOffset !== undefined) {
    if (isZero(change.planeOffset)) scratch.setText(piece, 'PlaneOffset', null);
    else scratch.setNumber(piece, 'PlaneOffset', change.planeOffset);
  }
  return scratch.xmlOf(piece);
}
